import React, { useEffect, useMemo, useState } from 'react';
import { Icon, Platform } from './Icons.jsx';
import { api } from '../services/api/client.js';
import { DeviceTimezoneLabel } from './DeviceTimezoneLabel.jsx';

const PLATFORM_LABELS = {
  instagram: 'Instagram',
  threads: 'Threads',
  telegram: 'Telegram',
  x: 'X',
  twitter: 'X',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
};

function platformLabel(platform) {
  return PLATFORM_LABELS[platform] || platform;
}

function normalizePlatform(platform) {
  return platform === 'twitter' ? 'x' : platform;
}

function bestTimeDate() {
  const now = new Date();
  now.setHours(now.getHours() + 1, 0, 0, 0);
  return now;
}

function toDatetimeLocalValue(date) {
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function datetimeLocalToIso(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function RepublishDialog({ open, content, onClose, onComplete }) {
  const item = content?.contentItem || null;
  const status = item?.status || 'draft';
  const contentId = item?.id || null;
  const versionId = content?.activeVersion?.id || null;
  const isPublished = status === 'published';
  const platforms = useMemo(() => {
    const list = Array.isArray(content?.publishPlatforms) ? content.publishPlatforms : [];
    if (list.length) return list.map(normalizePlatform);
    const fromVersion = Object.keys(content?.activeVersion?.platformVersions || {}).filter(key => !key.startsWith('__'));
    return fromVersion.length ? fromVersion : ['linkedin'];
  }, [content]);

  const [accounts, setAccounts] = useState([]);
  const [registry, setRegistry] = useState({});
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);
  const [whenMode, setWhenMode] = useState('now');
  const [customAt, setCustomAt] = useState(toDatetimeLocalValue(bestTimeDate()));
  const [regenerateOn, setRegenerateOn] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setStage(null);
    setWhenMode('now');
    setCustomAt(toDatetimeLocalValue(bestTimeDate()));
    setRegenerateOn(false);
    setInstructions('');
  }, [open, contentId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingTargets(true);
    Promise.all([api.socialAccounts.list(), api.platforms.registry()])
      .then(([accountResponse, registryResponse]) => {
        if (cancelled) return;
        const reg = Object.fromEntries((registryResponse.platforms || []).map(p => [normalizePlatform(p.platform), p]));
        const all = (accountResponse.accounts || []).slice().sort((a, b) => sortAccountsByOriginalPlatforms(a, b, platforms));
        const originalPlatformSet = new Set(platforms);
        const preselect = all.filter(account => isAccountReady(account, reg) && originalPlatformSet.has(normalizePlatform(account.platform)));
        setRegistry(reg);
        setAccounts(all);
        setSelectedTargetIds(preselect.map(account => account.id));
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Unable to load accounts'); })
      .finally(() => { if (!cancelled) setLoadingTargets(false); });
    return () => { cancelled = true; };
  }, [open, platforms.join('|')]);

  if (!open) return null;

  function toggleTarget(id) {
    setSelectedTargetIds(values => values.includes(id) ? values.filter(value => value !== id) : [...values, id]);
  }

  function toggleAllReadyTargets() {
    const readyIds = accounts.filter(account => isAccountReady(account, registry)).map(account => account.id);
    setSelectedTargetIds(values => values.length === readyIds.length ? [] : readyIds);
  }

  function selectedPlatforms() {
    const platformSet = new Set();
    const accountsById = new Map(accounts.map(account => [account.id, account]));
    selectedTargetIds.forEach(id => {
      const account = accountsById.get(id);
      if (account) platformSet.add(normalizePlatform(account.platform));
    });
    return [...platformSet];
  }

  async function submit() {
    if (!contentId) {
      setError('Pick a content item first');
      return;
    }
    if (!selectedTargetIds.length) {
      setError('Choose at least one publish-ready account');
      return;
    }
    let scheduledAt;
    if (whenMode === 'now') {
      scheduledAt = new Date().toISOString();
    } else if (whenMode === 'best') {
      scheduledAt = bestTimeDate().toISOString();
    } else {
      const iso = datetimeLocalToIso(customAt);
      if (!iso) {
        setError('Pick a valid future time');
        return;
      }
      scheduledAt = iso;
    }
    setWorking(true);
    setError(null);

    try {
      let workingContentId = contentId;
      let workingVersionId = versionId;
      let workingContent = content;

      if (isPublished) {
        setStage('Duplicating post…');
        const duplicate = await api.content.duplicate(contentId);
        workingContentId = duplicate?.contentItem?.id || duplicate?.id || contentId;
        workingVersionId = duplicate?.activeVersion?.id || workingVersionId;
        workingContent = duplicate?.contentItem ? duplicate : workingContent;
      }

      const targetPlatforms = selectedPlatforms();

      if (regenerateOn) {
        setStage('Regenerating with AI…');
        const regen = await api.content.regenerate(workingContentId, {
          mode: 'full',
          targetPlatforms: targetPlatforms.length ? targetPlatforms : platforms,
          instructions: instructions.trim() || undefined,
          confirmCreditSpend: true,
        });
        workingVersionId = regen?.activeVersion?.id || workingVersionId;
        workingContent = regen?.contentItem ? regen : workingContent;
      }

      if (!workingVersionId) {
        const refreshed = await api.content.get(workingContentId);
        workingVersionId = refreshed?.activeVersion?.id || refreshed?.contentVersion?.id || workingVersionId;
        workingContent = refreshed?.contentItem ? refreshed : workingContent;
      }

      setStage('Scheduling targets…');
      const accountsById = new Map(accounts.map(account => [account.id, account]));
      const targets = selectedTargetIds
        .map(id => accountsById.get(id))
        .filter(Boolean)
        .map(account => ({
          platform: normalizePlatform(account.platform),
          socialAccountId: account.id,
          priority: whenMode === 'now' ? 'now' : 'scheduled',
          scheduledAt,
        }));

      const result = await api.scheduler.create({
        contentItemId: workingContentId,
        contentVersionId: workingVersionId,
        scheduledAt,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        mode: whenMode === 'now' ? 'publish_now' : 'manual',
        targets,
      });

      let scheduledContent = workingContent;
      try {
        const refreshed = await api.content.get(workingContentId);
        scheduledContent = refreshed?.contentItem ? refreshed : scheduledContent;
      } catch (_) {
        scheduledContent = workingContent;
      }
      setStage('Done');
      onComplete?.({ schedule: result, scheduledPost: result.post || result, contentId: workingContentId, contentVersionId: workingVersionId, content: scheduledContent });
      onClose?.();
    } catch (err) {
      setError(err.message || 'Unable to republish');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="republish-dialog-backdrop" role="dialog" aria-modal="true" onClick={() => !working && onClose?.()}>
      <div className="republish-dialog" onClick={event => event.stopPropagation()}>
        <header className="republish-head">
          <div>
            <div className="eyebrow">{isPublished ? 'Republish' : 'Schedule again'}</div>
            <h3 className="h3">{isPublished ? 'Send a fresh copy' : 'Re-schedule this post'}</h3>
            <p className="caption">{isPublished ? 'Original post stays untouched. We duplicate it before scheduling.' : 'Pick when and where this post goes out next.'}</p>
          </div>
          <button className="btn btn-ghost btn-icon" aria-label="Close" disabled={working} onClick={() => onClose?.()}><Icon name="x" size={14} /></button>
        </header>

        {error && <div className="auth-error"><Icon name="alert" size={14} /> {error}</div>}

        <section className="republish-section">
          <div className="republish-section-head">
            <div>
              <div className="label">AI rewrite</div>
              <div className="caption">Toggle on to regenerate the copy before scheduling</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={regenerateOn}
              className={'republish-toggle ' + (regenerateOn ? 'is-on' : '')}
              onClick={() => setRegenerateOn(value => !value)}
              disabled={working}
            >
              <span className="republish-toggle-knob" />
            </button>
          </div>
          {regenerateOn && (
            <textarea
              className="field republish-instructions"
              rows={3}
              placeholder="Optional: how should the AI rewrite this? e.g. punchier hook, add CTA, more casual tone."
              value={instructions}
              onChange={event => setInstructions(event.target.value)}
              disabled={working}
            />
          )}
        </section>

        <section className="republish-section">
          <div className="republish-section-head">
            <div>
              <div className="label">Where to publish</div>
              <div className="caption">Pick any connected account — original platforms shown first.</div>
            </div>
            {accounts.length > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleAllReadyTargets()} disabled={working}>
                {selectedTargetIds.length === accounts.filter(a => isAccountReady(a, registry)).length ? 'Clear all' : 'Select all ready'}
              </button>
            )}
          </div>
          {loadingTargets ? (
            <div className="caption">Loading connected accounts…</div>
          ) : accounts.length === 0 ? (
            <div className="caption">No connected accounts yet. Connect one in Social accounts first.</div>
          ) : (
            <div className="republish-target-grid">
              {accounts.map(account => {
                const ready = isAccountReady(account, registry);
                const selected = selectedTargetIds.includes(account.id);
                const isOriginal = platforms.includes(normalizePlatform(account.platform));
                return (
                  <button
                    type="button"
                    key={account.id}
                    className={'republish-target ' + (selected ? 'is-on ' : '') + (!ready ? 'is-disabled' : '')}
                    onClick={() => ready && toggleTarget(account.id)}
                    disabled={!ready || working}
                  >
                    <Platform name={normalizePlatform(account.platform)} size={20} />
                    <div className="republish-target-text">
                      <span className="republish-target-name">{account.displayName || platformLabel(account.platform)}</span>
                      <span className="caption">
                        {ready ? platformLabel(account.platform) : 'Reconnect required'}
                        {isOriginal && <span className="republish-original-tag"> · Original</span>}
                      </span>
                    </div>
                    {selected && <Icon name="check" size={14} />}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="republish-section">
          <div className="label">When</div>
          <div className="republish-when-row">
            <button type="button" className={'republish-when-pill ' + (whenMode === 'now' ? 'is-on' : '')} onClick={() => setWhenMode('now')} disabled={working}>
              <Icon name="sparkles" size={13} /> Now
            </button>
            <button type="button" className={'republish-when-pill ' + (whenMode === 'best' ? 'is-on' : '')} onClick={() => setWhenMode('best')} disabled={working}>
              <Icon name="flame" size={13} /> Best time today
            </button>
            <button type="button" className={'republish-when-pill ' + (whenMode === 'custom' ? 'is-on' : '')} onClick={() => setWhenMode('custom')} disabled={working}>
              <Icon name="calendar" size={13} /> Custom
            </button>
          </div>
          {whenMode === 'custom' && (
            <label>
              <DeviceTimezoneLabel />
              <input
                className="input"
                type="datetime-local"
                value={customAt}
                min={toDatetimeLocalValue(new Date(Date.now() + 60 * 1000))}
                onChange={event => setCustomAt(event.target.value)}
                disabled={working}
              />
            </label>
          )}
        </section>

        <footer className="republish-foot">
          <span className="caption">{stage || (selectedTargetIds.length ? `${selectedTargetIds.length} target${selectedTargetIds.length === 1 ? '' : 's'} selected` : 'Pick at least one account')}</span>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => onClose?.()} disabled={working}>Cancel</button>
            <button className="btn btn-primary" onClick={submit} disabled={working || !selectedTargetIds.length}>
              {working ? 'Working…' : isPublished ? 'Duplicate & schedule' : whenMode === 'now' ? 'Publish now' : 'Schedule'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function isAccountReady(account, registry) {
  const platform = normalizePlatform(account.platform);
  if (account.status !== 'connected') return false;
  if (registry[platform] && !registry[platform].supportsPublish) return false;
  if (platform === 'telegram' && account.capabilities?.destinationType && account.capabilities.destinationType !== 'channel') return false;
  return true;
}

function sortAccountsByOriginalPlatforms(a, b, originalPlatforms) {
  const set = new Set(originalPlatforms);
  const aOriginal = set.has(normalizePlatform(a.platform)) ? 0 : 1;
  const bOriginal = set.has(normalizePlatform(b.platform)) ? 0 : 1;
  if (aOriginal !== bOriginal) return aOriginal - bOriginal;
  return platformLabel(a.platform).localeCompare(platformLabel(b.platform));
}

export default RepublishDialog;
